import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import Select, { createFilter } from "react-select";
import { useGet } from "../../api";
import { ICouponPartnersResponse } from "../../interfaces";

interface PartnerOption {
  value: string;
  label: string;
}

// Matches on the name or a pasted partner id.
const filterOption = createFilter<PartnerOption>({
  stringify: (o) => `${o.label} ${o.value}`,
});

/** Partner picker over the cached Partners list; cleared means every partner. */
export const PartnerTypeahead: React.FC<{
  partnerId: string;
  onChange: (partnerId: string, partnerName: string) => void;
}> = ({ partnerId, onChange }) => {
  const { data, loading, error } = useGet<ICouponPartnersResponse>(
    "/coupon-performance/partners"
  );
  const options = useMemo(
    () =>
      (data?.items ?? []).map((p) => ({ value: p.partnerId, label: p.name })),
    [data]
  );
  const selected =
    options.find((o) => o.value === partnerId) ??
    (partnerId ? { value: partnerId, label: partnerId } : null);

  return (
    <Form.Group
      title={
        error
          ? `Partner list unavailable: ${error.message}`
          : "Only traffic from this partner app. Clear it for every partner."
      }
    >
      <Form.Label className="small mb-1">Partner</Form.Label>
      <Select<PartnerOption>
        options={options}
        value={selected}
        isLoading={loading}
        isClearable
        filterOption={filterOption}
        onChange={(opt) => onChange(opt?.value ?? "", opt?.label ?? "")}
        placeholder={error ? "All partners (list unavailable)" : "All partners"}
        noOptionsMessage={() => "No partner matches"}
        styles={{
          control: (base) => ({ ...base, minHeight: 31, fontSize: 13 }),
          valueContainer: (base) => ({ ...base, padding: "0 6px" }),
          input: (base) => ({ ...base, margin: 0, padding: 0 }),
          indicatorsContainer: (base) => ({
            ...base,
            "> div": { padding: 4 },
          }),
        }}
      />
    </Form.Group>
  );
};
